import React from 'react';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1];
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

/**
 * PublicHero — asymmetric, high-contrast marketing hero.
 *
 * Left column (3/5): oversized Poppins-black headline + copy + CTAs.
 * Right column (2/5): media / lead-capture card.
 * Full-bleed darkened background photo with a True North diagonal texture.
 */
export default function PublicHero({
  eyebrow,
  title,
  description,
  bullets = [],
  actions,
  media,
  backgroundImage,
  wrapperAs = 'section',
  testId = 'public-hero',
}) {
  const Wrapper = wrapperAs;

  return (
    <Wrapper
      className="relative w-full overflow-hidden px-4 sm:px-6 pt-16 pb-20 md:pt-24 md:pb-28"
      data-testid={testId}
    >
      {backgroundImage?.src && (
        <>
          <img
            src={backgroundImage.src}
            alt={backgroundImage.alt || ''}
            aria-hidden={!backgroundImage.alt}
            loading="eager"
            decoding="async"
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
            data-testid="public-hero-bg"
          />
          {/* Directional legibility gradient — darkest on the left/bottom */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none bg-gradient-to-r from-background via-background/85 to-background/40"
          />
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none bg-gradient-to-t from-background via-transparent to-transparent"
          />
          {/* True North diagonal texture */}
          <div aria-hidden className="absolute inset-0 pointer-events-none hc-northlines opacity-70" />
        </>
      )}

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 max-w-6xl mx-auto grid gap-12 md:gap-14 lg:gap-16 md:grid-cols-2 lg:grid-cols-5 md:items-center"
      >
        {/* Copy column */}
        <div className="lg:col-span-3 max-w-2xl">
          {eyebrow && (
            <motion.div variants={item} className="flex items-center gap-3 mb-6">
              <span aria-hidden className="h-4 w-1 bg-primary" />
              <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                {eyebrow}
              </p>
            </motion.div>
          )}
          {title && (
            <motion.h1
              variants={item}
              className="text-5xl sm:text-6xl lg:text-7xl font-heading font-black tracking-tighter text-foreground leading-[0.98]"
            >
              {title}
            </motion.h1>
          )}
          {description && (
            <motion.p
              variants={item}
              className="mt-6 text-base sm:text-lg text-foreground/70 leading-relaxed max-w-xl"
            >
              {description}
            </motion.p>
          )}

          {bullets.length > 0 && (
            <motion.ul
              variants={item}
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5"
            >
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex w-5 h-5 bg-primary text-primary-foreground items-center justify-center shrink-0">
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <span className="text-sm sm:text-base text-foreground/90 font-medium">{b}</span>
                </li>
              ))}
            </motion.ul>
          )}

          {actions && <motion.div variants={item} className="mt-9">{actions}</motion.div>}
        </div>

        {/* Media / form column */}
        {media && (
          <motion.div
            variants={item}
            className="lg:col-span-2 w-full max-w-md mx-auto md:mx-0 md:justify-self-end"
          >
            {media}
          </motion.div>
        )}
      </motion.div>
    </Wrapper>
  );
}
